import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, TrendingUp, Home, Phone, Mail, Award, 
  CheckCircle, ArrowRight, Heart, Plane, Building,
  PiggyBank, Briefcase, Calculator, Users, FileText
} from 'lucide-react';
import { useAgent } from '../context/AgentContext';
import { useLanguage } from '../context/LanguageContext';
import { TeamPhoto } from '../components/TeamPhoto';

const AgentHomePage = () => {
  const { agentSlug } = useParams();
  const { agent, loading, error, buildLink } = useAgent();
  const { language } = useLanguage();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0077B6] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Agent Not Found</h1>
          <p className="text-gray-600 mb-6">The agent you're looking for doesn't exist or is no longer active.</p>
          <Button asChild>
            <Link to="/">Return to Main Site</Link>
          </Button>
        </div>
      </div>
    );
  }

  // License helpers
  const licenses = agent.licenses || [];
  const hasMortgage = licenses.includes('mortgage');
  const hasInsurance = licenses.includes('insurance');
  const hasInvestments = licenses.includes('investments');
  const hasFinancial = hasInsurance || hasInvestments;
  const isMultiLicensed = hasMortgage && hasFinancial;

  // Dynamic tagline based on licenses
  const getTagline = () => {
    if (language === 'fr') {
      if (isMultiLicensed) {
        return "Votre Conseiller en Hypothèques, Assurance et Investissements";
      } else if (hasMortgage) {
        return "Votre Professionnel Hypothécaire de Confiance";
      } else if (hasFinancial) {
        return "Votre Conseiller en Assurance et Investissements";
      }
      return "Votre Conseiller Financier de Confiance";
    } else {
      if (isMultiLicensed) {
        return "Your Trusted Mortgage, Insurance & Investment Advisor";
      } else if (hasMortgage) {
        return "Your Trusted Mortgage Professional";
      } else if (hasFinancial) {
        return "Your Trusted Insurance & Investment Advisor";
      }
      return "Your Trusted Financial Advisor";
    }
  };

  // Dynamic intro based on licenses
  const getIntro = () => {
    if (language === 'fr') {
      if (isMultiLicensed) {
        return "Je suis là pour vous aider à réaliser vos rêves d'accession à la propriété et à protéger votre avenir financier. Que vous ayez besoin d'un prêt hypothécaire, d'une assurance vie ou de solutions d'investissement, je vous guiderai à chaque étape.";
      } else if (hasMortgage) {
        return "Je suis là pour vous aider à réaliser vos rêves d'accession à la propriété. Avec accès à plus de 50 prêteurs, je trouverai les meilleurs taux et conditions hypothécaires adaptés à votre situation.";
      } else {
        return "Je suis là pour vous aider à protéger ce qui compte le plus et à faire croître votre patrimoine. Des solutions d'assurance aux fonds distincts, je vous guiderai vers la sécurité financière.";
      }
    } else {
      if (isMultiLicensed) {
        return "I'm here to help you achieve your homeownership dreams and protect your financial future. Whether you need a mortgage, life insurance, or investment solutions, I'll guide you through every step.";
      } else if (hasMortgage) {
        return "I'm here to help you achieve your homeownership dreams. With access to 50+ lenders, I'll find the best mortgage rates and terms tailored to your unique situation.";
      } else {
        return "I'm here to help you protect what matters most and grow your wealth. From insurance solutions to segregated funds, I'll guide you toward financial security.";
      }
    }
  };

  // Translations
  const text = {
    en: {
      welcomePrefix: "Welcome! I'm",
      services: "My Services",
      // Insurance content
      insuranceTitle: "Insurance Solutions",
      insuranceDesc: "Life, critical illness, disability, and travel insurance to protect you and your family.",
      lifeInsurance: "Life Insurance",
      lifeInsuranceDesc: "Protect your loved ones with comprehensive term, whole life, or universal life coverage.",
      criticalIllness: "Critical Illness",
      criticalIllnessDesc: "Lump-sum payment upon diagnosis of covered conditions like cancer or heart attack.",
      travelInsurance: "Travel Insurance",
      travelInsuranceDesc: "Medical emergencies, trip cancellation, and visitor to Canada coverage.",
      disabilityInsurance: "Disability Insurance",
      disabilityInsuranceDesc: "Income replacement if you're unable to work due to illness or injury.",
      // Investment content
      investmentsTitle: "Investment Solutions",
      investmentsDesc: "RRSPs, TFSAs, RESPs, and segregated funds to grow your wealth.",
      rrsp: "RRSP",
      rrspDesc: "Tax-deductible contributions that grow tax-free until retirement.",
      tfsa: "TFSA",
      tfsaDesc: "Flexible savings with completely tax-free growth and withdrawals.",
      resp: "RESP",
      respDesc: "Save for education with government grants up to $7,200.",
      segFunds: "Segregated Funds",
      segFundsDesc: "Insurance-based investments with principal guarantees.",
      // Mortgage content
      mortgageTitle: "Mortgage Services",
      mortgageDesc: "Access to 50+ lenders for the best mortgage rates and terms.",
      purchase: "Home Purchase",
      purchaseDesc: "First-time buyers or upgrading - find your perfect mortgage solution.",
      refinance: "Refinancing",
      refinanceDesc: "Access your home equity for renovations, debt consolidation, or investments.",
      renewal: "Mortgage Renewal",
      renewalDesc: "Don't just sign at your current rate - let me find you a better deal.",
      selfEmployed: "Self-Employed",
      selfEmployedDesc: "Specialized programs for business owners with non-traditional income.",
      // Why work with me - dynamic based on license
      whyWork: "Why Work With Me?",
      reason1Insurance: "Access to Canada's top insurance carriers",
      reason2Insurance: "Expert guidance through policy selection",
      reason3Insurance: "Regular policy reviews to ensure adequate coverage",
      reason4Insurance: "Claims support when you need it most",
      reason1Mortgage: "Access to 50+ lenders for the best rates",
      reason2Mortgage: "Expert guidance through the mortgage process",
      reason3Mortgage: "Ongoing support from pre-approval to closing",
      reason4Mortgage: "Renewal reminders and rate monitoring",
      reason1Both: "One-stop shop for all your financial needs",
      reason2Both: "Access to top lenders and insurance carriers",
      reason3Both: "Comprehensive financial planning approach",
      reason4Both: "Ongoing support and regular reviews",
      cta: "Let's Start a Conversation",
      ctaDesc: "Ready to secure your financial future? Contact me today for a free consultation.",
      ctaDescMortgage: "Ready to achieve your homeownership dreams? Contact me today for a free consultation.",
      ctaDescInsurance: "Ready to protect what matters most? Contact me today for a free consultation.",
      callMe: "Call Me",
      emailMe: "Email Me",
      learnMore: "Learn More",
      getQuote: "Get a Quote",
      applyNow: "Apply Now"
    },
    fr: {
      welcomePrefix: "Bienvenue! Je suis",
      services: "Mes Services",
      // Insurance content
      insuranceTitle: "Solutions d'Assurance",
      insuranceDesc: "Assurance vie, maladies graves, invalidité et voyage pour vous protéger.",
      lifeInsurance: "Assurance Vie",
      lifeInsuranceDesc: "Protégez vos proches avec une couverture temporaire, vie entière ou universelle.",
      criticalIllness: "Maladies Graves",
      criticalIllnessDesc: "Paiement forfaitaire lors du diagnostic de conditions couvertes.",
      travelInsurance: "Assurance Voyage",
      travelInsuranceDesc: "Urgences médicales, annulation de voyage et visiteurs au Canada.",
      disabilityInsurance: "Assurance Invalidité",
      disabilityInsuranceDesc: "Remplacement de revenu en cas d'incapacité de travail.",
      // Investment content
      investmentsTitle: "Solutions d'Investissement",
      investmentsDesc: "REER, CELI, REEE et fonds distincts pour faire croître votre patrimoine.",
      rrsp: "REER",
      rrspDesc: "Contributions déductibles d'impôt qui croissent à l'abri de l'impôt.",
      tfsa: "CELI",
      tfsaDesc: "Épargne flexible avec croissance et retraits libres d'impôt.",
      resp: "REEE",
      respDesc: "Épargnez pour l'éducation avec subventions jusqu'à 7 200$.",
      segFunds: "Fonds Distincts",
      segFundsDesc: "Investissements avec garanties du capital et protection.",
      // Mortgage content
      mortgageTitle: "Services Hypothécaires",
      mortgageDesc: "Accès à plus de 50 prêteurs pour les meilleurs taux.",
      purchase: "Achat de Maison",
      purchaseDesc: "Premier achat ou mise à niveau - trouvez votre solution hypothécaire.",
      refinance: "Refinancement",
      refinanceDesc: "Accédez à l'équité pour rénovations ou consolidation de dettes.",
      renewal: "Renouvellement",
      renewalDesc: "Ne signez pas au taux actuel - laissez-moi trouver mieux.",
      selfEmployed: "Travailleurs Autonomes",
      selfEmployedDesc: "Programmes spécialisés pour propriétaires d'entreprise.",
      // Why work with me
      whyWork: "Pourquoi Travailler Avec Moi?",
      reason1Insurance: "Accès aux meilleurs assureurs du Canada",
      reason2Insurance: "Conseils experts pour le choix de polices",
      reason3Insurance: "Révisions régulières de couverture",
      reason4Insurance: "Support aux réclamations",
      reason1Mortgage: "Accès à plus de 50 prêteurs",
      reason2Mortgage: "Conseils experts tout au long du processus",
      reason3Mortgage: "Support de la pré-approbation à la clôture",
      reason4Mortgage: "Rappels de renouvellement et suivi des taux",
      reason1Both: "Guichet unique pour tous vos besoins financiers",
      reason2Both: "Accès aux meilleurs prêteurs et assureurs",
      reason3Both: "Approche de planification financière complète",
      reason4Both: "Support continu et révisions régulières",
      cta: "Commençons une Conversation",
      ctaDesc: "Prêt à sécuriser votre avenir financier? Contactez-moi aujourd'hui.",
      ctaDescMortgage: "Prêt à réaliser vos rêves d'accession? Contactez-moi aujourd'hui.",
      ctaDescInsurance: "Prêt à protéger ce qui compte? Contactez-moi aujourd'hui.",
      callMe: "Appelez-moi",
      emailMe: "Écrivez-moi",
      learnMore: "En Savoir Plus",
      getQuote: "Obtenir une Soumission",
      applyNow: "Postuler Maintenant"
    }
  };

  const t = text[language];

  // Get "Why Work With Me" reasons based on license
  const getReasons = () => {
    if (isMultiLicensed) {
      return [t.reason1Both, t.reason2Both, t.reason3Both, t.reason4Both];
    } else if (hasMortgage) {
      return [t.reason1Mortgage, t.reason2Mortgage, t.reason3Mortgage, t.reason4Mortgage];
    } else {
      return [t.reason1Insurance, t.reason2Insurance, t.reason3Insurance, t.reason4Insurance];
    }
  };

  // Get CTA description based on license
  const getCtaDesc = () => {
    if (isMultiLicensed) return t.ctaDesc;
    if (hasMortgage) return t.ctaDescMortgage;
    return t.ctaDescInsurance;
  };

  const reasons = getReasons();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Agent Photo */}
      <section className="relative bg-gradient-to-br from-[#003D5C] to-[#0077B6] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Agent Info */}
            <div className="text-center md:text-left order-2 md:order-1">
              <p className="text-blue-200 text-lg mb-2">{t.welcomePrefix}</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                {agent.name}
              </h1>
              <p className="text-lg text-blue-200 mb-4">
                {agent.role}
              </p>
              <p className="text-xl md:text-2xl text-[#D4AF37] font-medium mb-6">
                {getTagline()}
              </p>
              <p className="text-blue-100 text-lg mb-8 max-w-xl">
                {getIntro()}
              </p>
              
              {/* Contact Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold"
                >
                  <a href={`tel:${agent.phone}`}>
                    <Phone className="h-5 w-5 mr-2" />
                    {t.callMe}: {agent.phone}
                  </a>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-[#003D5C] font-bold"
                >
                  <a href={`mailto:${agent.email}`}>
                    <Mail className="h-5 w-5 mr-2" />
                    {t.emailMe}
                  </a>
                </Button>
              </div>
            </div>
            
            {/* Agent Photo */}
            <div className="flex justify-center order-1 md:order-2">
              <div className="relative">
                <div className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-4 border-[#D4AF37] shadow-2xl">
                  <TeamPhoto src={agent.photo} name={agent.name} className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-[#D4AF37] rounded-full p-3 shadow-lg">
                  <Award className="h-8 w-8 text-[#003D5C]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section - Shows relevant services based on license */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t.services}</h2>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto"></div>
          </div>
          
          {/* Mortgage Only - Single centered card */}
          {hasMortgage && !hasFinancial && (
            <div className="flex justify-center">
              <Card className="bg-[#F0F7FF] border-none hover:shadow-lg transition-shadow max-w-md w-full">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-[#003D5C] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Home className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{t.mortgageTitle}</h3>
                  <p className="text-gray-600 mb-6 text-lg">{t.mortgageDesc}</p>
                  <Button asChild className="bg-[#003D5C] hover:bg-[#002A40]">
                    <Link to={buildLink('/mortgage')}>
                      {t.learnMore} <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Insurance/Investments Only - Centered cards */}
          {hasFinancial && !hasMortgage && (
            <div className="flex flex-wrap justify-center gap-8">
              {/* Insurance Card */}
              <Card className="bg-[#F0F7FF] border-none hover:shadow-lg transition-shadow max-w-md w-full">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Shield className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{t.insuranceTitle}</h3>
                  <p className="text-gray-600 mb-6 text-lg">{t.insuranceDesc}</p>
                  <Button asChild className="bg-[#0077B6] hover:bg-[#005A8C]">
                    <Link to={buildLink('/insurance')}>
                      {t.learnMore} <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              
              {/* Investments Card - only if has investments license */}
              {hasInvestments && (
                <Card className="bg-[#F0F7FF] border-none hover:shadow-lg transition-shadow max-w-md w-full">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
                      <TrendingUp className="h-10 w-10 text-[#003D5C]" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">{t.investmentsTitle}</h3>
                    <p className="text-gray-600 mb-6 text-lg">{t.investmentsDesc}</p>
                    <Button asChild className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E]">
                      <Link to={buildLink('/investment')}>
                        {t.learnMore} <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Multi-Licensed - All three cards in grid */}
          {isMultiLicensed && (
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-[#F0F7FF] border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{t.insuranceTitle}</h3>
                  <p className="text-gray-600 mb-4">{t.insuranceDesc}</p>
                  <Button asChild variant="link" className="text-[#0077B6]">
                    <Link to={buildLink('/insurance')}>
                      {t.learnMore} <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="bg-[#F0F7FF] border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
                    <TrendingUp className="h-8 w-8 text-[#003D5C]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{t.investmentsTitle}</h3>
                  <p className="text-gray-600 mb-4">{t.investmentsDesc}</p>
                  <Button asChild variant="link" className="text-[#0077B6]">
                    <Link to={buildLink('/investment')}>
                      {t.learnMore} <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="bg-[#F0F7FF] border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#003D5C] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Home className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{t.mortgageTitle}</h3>
                  <p className="text-gray-600 mb-4">{t.mortgageDesc}</p>
                  <Button asChild variant="link" className="text-[#0077B6]">
                    <Link to={buildLink('/mortgage')}>
                      {t.learnMore} <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Detailed Services - Insurance (only for insurance/investment licensed agents) */}
      {hasFinancial && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t.insuranceTitle}</h2>
              <div className="h-1 w-24 bg-[#D4AF37] mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#0077B6]">
                <CardContent className="p-6">
                  <Heart className="h-10 w-10 text-[#0077B6] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.lifeInsurance}</h3>
                  <p className="text-gray-600 text-sm">{t.lifeInsuranceDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#0077B6]">
                <CardContent className="p-6">
                  <Shield className="h-10 w-10 text-[#0077B6] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.criticalIllness}</h3>
                  <p className="text-gray-600 text-sm">{t.criticalIllnessDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#0077B6]">
                <CardContent className="p-6">
                  <Plane className="h-10 w-10 text-[#0077B6] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.travelInsurance}</h3>
                  <p className="text-gray-600 text-sm">{t.travelInsuranceDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#0077B6]">
                <CardContent className="p-6">
                  <FileText className="h-10 w-10 text-[#0077B6] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.disabilityInsurance}</h3>
                  <p className="text-gray-600 text-sm">{t.disabilityInsuranceDesc}</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Button asChild className="bg-[#0077B6] hover:bg-[#005A8C]">
                <Link to={buildLink('/insurance')}>
                  {t.getQuote} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Detailed Services - Investments (only for investment licensed agents) */}
      {hasInvestments && (
        <section className={`py-16 ${hasFinancial && !hasMortgage ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t.investmentsTitle}</h2>
              <div className="h-1 w-24 bg-[#D4AF37] mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#D4AF37]">
                <CardContent className="p-6">
                  <PiggyBank className="h-10 w-10 text-[#D4AF37] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.rrsp}</h3>
                  <p className="text-gray-600 text-sm">{t.rrspDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#D4AF37]">
                <CardContent className="p-6">
                  <TrendingUp className="h-10 w-10 text-[#D4AF37] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.tfsa}</h3>
                  <p className="text-gray-600 text-sm">{t.tfsaDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#D4AF37]">
                <CardContent className="p-6">
                  <Users className="h-10 w-10 text-[#D4AF37] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.resp}</h3>
                  <p className="text-gray-600 text-sm">{t.respDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#D4AF37]">
                <CardContent className="p-6">
                  <Briefcase className="h-10 w-10 text-[#D4AF37] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.segFunds}</h3>
                  <p className="text-gray-600 text-sm">{t.segFundsDesc}</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Button asChild className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E]">
                <Link to={buildLink('/investment')}>
                  {t.learnMore} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Detailed Services - Mortgage (only for mortgage licensed agents) */}
      {hasMortgage && (
        <section className={`py-16 ${isMultiLicensed ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t.mortgageTitle}</h2>
              <div className="h-1 w-24 bg-[#D4AF37] mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#003D5C]">
                <CardContent className="p-6">
                  <Home className="h-10 w-10 text-[#003D5C] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.purchase}</h3>
                  <p className="text-gray-600 text-sm">{t.purchaseDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#003D5C]">
                <CardContent className="p-6">
                  <Calculator className="h-10 w-10 text-[#003D5C] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.refinance}</h3>
                  <p className="text-gray-600 text-sm">{t.refinanceDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#003D5C]">
                <CardContent className="p-6">
                  <FileText className="h-10 w-10 text-[#003D5C] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.renewal}</h3>
                  <p className="text-gray-600 text-sm">{t.renewalDesc}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-[#003D5C]">
                <CardContent className="p-6">
                  <Building className="h-10 w-10 text-[#003D5C] mb-4" />
                  <h3 className="font-bold text-gray-800 mb-2">{t.selfEmployed}</h3>
                  <p className="text-gray-600 text-sm">{t.selfEmployedDesc}</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Button asChild className="bg-[#003D5C] hover:bg-[#002A40]">
                <Link to={buildLink('/mortgage')}>
                  {t.applyNow} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Why Work With Me Section */}
      <section className={`py-16 ${hasMortgage && !hasFinancial ? 'bg-white' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t.whyWork}</h2>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto"></div>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {reasons.map((reason) => (
              <div key={reason} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-[#0077B6]" />
                </div>
                <p className="text-gray-700">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Licensing & Compliance Section */}
      {(licenses.length > 0 || agent.license_details) && (
        <section className="py-12 bg-white border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                {language === 'fr' ? 'Licences et Accréditations' : 'Licenses & Credentials'}
              </h2>
              <div className="h-1 w-24 bg-[#D4AF37] mx-auto"></div>
            </div>
            
            <div className="bg-[#F0F7FF] rounded-xl p-6 border border-blue-100">
              {licenses.length > 0 && (
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {hasMortgage && (
                    <div className="flex items-center gap-2 bg-[#003D5C] text-white px-4 py-2 rounded-full">
                      <Home className="h-5 w-5" />
                      <span className="font-semibold">
                        {language === 'fr' ? 'Professionnel Hypothécaire Licencié' : 'Licensed Mortgage Professional'}
                      </span>
                    </div>
                  )}
                  {hasInsurance && (
                    <div className="flex items-center gap-2 bg-[#0077B6] text-white px-4 py-2 rounded-full">
                      <Shield className="h-5 w-5" />
                      <span className="font-semibold">
                        {language === 'fr' ? 'Conseiller en Assurance Licencié' : 'Licensed Insurance Advisor'}
                      </span>
                    </div>
                  )}
                  {hasInvestments && (
                    <div className="flex items-center gap-2 bg-[#D4AF37] text-[#003D5C] px-4 py-2 rounded-full">
                      <TrendingUp className="h-5 w-5" />
                      <span className="font-semibold">
                        {language === 'fr' ? 'Conseiller en Investissement Licencié' : 'Licensed Investment Advisor'}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {agent.license_details && (
                <div className="text-center">
                  <p className="text-gray-700 whitespace-pre-line">{agent.license_details}</p>
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-blue-200 text-center">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">{language === 'fr' ? 'Provinces autorisées:' : 'Licensed in:'}</span> {agent.provinces}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.cta}</h2>
          <p className="text-blue-100 text-lg mb-8">{getCtaDesc()}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold text-lg"
            >
              <a href={`tel:${agent.phone}`}>
                <Phone className="h-5 w-5 mr-2" />
                {agent.phone}
              </a>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-[#0077B6] font-bold text-lg"
            >
              <a href={`mailto:${agent.email}`}>
                <Mail className="h-5 w-5 mr-2" />
                {agent.email}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Compliance Footer Section */}
      <section className="py-8 bg-[#003D5C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-blue-200 text-sm">
            {agent.name} {language === 'fr' ? 'est un représentant licencié de' : 'is a licensed representative with'}
          </p>
          <p className="text-white font-bold text-lg mt-1">
            {hasMortgage 
              ? 'Dominion Lending Centres Utmost Financial' 
              : 'Utmost Financial Inc.'}
            {hasMortgage && hasFinancial && (
              <span> & Utmost Financial Inc.</span>
            )}
          </p>
          {licenses.length > 0 && (
            <p className="text-blue-300 text-xs mt-2">
              {hasMortgage && 'Mortgage Brokerage License'}
              {hasMortgage && hasInsurance && ' • '}
              {hasInsurance && 'Life & Health Insurance License'}
              {(hasMortgage || hasInsurance) && hasInvestments && ' • '}
              {hasInvestments && 'Segregated Funds License'}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AgentHomePage;
